import { useCallback } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { useFocusEffect, useRouter } from 'expo-router';

import { Colors } from '@/constants/theme';
import { Spacing } from '@/constants/spacing';
import { getBodyFont } from '@/constants/typography';
import { useColorScheme } from '@/hooks/use-color-scheme';
import { useRouteMapModel } from '@/hooks/use-route-map-model';
import { RouteMapView } from '@/components/route-planner/map/RouteMapView';
import { RouteStepPager } from '@/components/route-planner/RouteStepPager';

const MINIMAP_HEIGHT = 120;

/**
 * TodayRouteCard — the home screen's route tracker: minimap banner with the
 * current step's pin highlighted (expand chip opens the full-screen map),
 * then the step-at-a-time pager. Renders nothing without an active plan.
 */
export function TodayRouteCard() {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];
  const router = useRouter();
  const { plan, progress, currentLegIndex, stopsById, toggleLeg, skipLeg, refresh, model } =
    useRouteMapModel();

  useFocusEffect(
    useCallback(() => {
      refresh();
    }, [refresh]),
  );

  const openFullMap = useCallback(() => {
    router.push('/route-planner/map');
  }, [router]);

  if (!plan || !progress) return null;

  return (
    <View style={styles.section}>
      <View style={styles.header}>
        <Text style={[styles.sectionTitle, { color: palette.primary, fontFamily: getBodyFont() }]}>
          Today&apos;s Route
        </Text>
        <Text
          style={[styles.progressCount, { color: palette.textMuted, fontFamily: getBodyFont() }]}
        >
          {progress.completed} of {progress.total}
        </Text>
      </View>

      {model && (
        <RouteMapView
          model={model}
          interactive={false}
          height={MINIMAP_HEIGHT}
          onExpandPress={openFullMap}
        />
      )}

      <RouteStepPager
        plan={plan}
        currentLegIndex={currentLegIndex}
        stopsById={stopsById}
        completedLegIds={plan.completedLegIds}
        onToggle={toggleLeg}
        onSkip={skipLeg}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  section: {
    gap: Spacing.sm,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: '600',
  },
  progressCount: {
    fontSize: 13,
  },
});
